import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Tabs,
  Tab,
  Badge,
  Divider,
  IconButton,
  TextField,
  InputAdornment,
  TablePagination,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Drawer,
  Checkbox,
  FormGroup,
  FormControlLabel,
  ListItemText
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import WorkRequestHandler from '../components/work-requests/WorkRequestHandler';
import RequesterFeedbackList from '../components/work-requests/RequesterFeedbackList';
import NewWorkRequestForm from '../components/work-requests/NewWorkRequestForm';
import { db } from '../firebase/config';
import { collection, query, where, orderBy, getDocs, doc, getDoc, updateDoc, limit, startAfter, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { COLLECTIONS } from '../firebase/collections';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import SortIcon from '@mui/icons-material/Sort';
import TuneIcon from '@mui/icons-material/Tune';
import NotificationBell from '../components/notifications/NotificationBell';
import HistoryIcon from '@mui/icons-material/History';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CreateIcon from '@mui/icons-material/Create';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import { sectorService } from '../services/sectorService';
import { PROBLEM_TRANSLATIONS, STATUS_LABELS, STATUS_COLORS } from '../models/WorkRequest';

const RequesterDashboard = () => {
  const [requests, setRequests] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const lastVisibleRef = useRef(null);
  const cacheTimeoutRef = useRef(null);
  const cachedRequestsRef = useRef(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [feedbackTabValue, setFeedbackTabValue] = useState(0);
  const [stats, setStats] = useState({
    byStatus: {},
    byPriority: {},
    byMonth: [],
    avgResolutionTime: 0,
    totalRequests: 0,
    pendingRequests: 0,
    completedRequests: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingValidations, setPendingValidations] = useState([]);
  const [recentlyValidated, setRecentlyValidated] = useState([]);
  const [loadingValidations, setLoadingValidations] = useState(false);
  const [validationServices, setValidationServices] = useState([]);
  const [validationPriority, setValidationPriority] = useState({});
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filtres avancés
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: [],
    priority: [],
    category: [],
    dateRange: {
      start: null,
      end: null
    }
  });
  
  // Tri
  const [sortConfig, setSortConfig] = useState({
    key: 'createdAt',
    direction: 'desc'
  });
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const { currentUser, userRole, userService } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  // Style pour les onglets
  const tabStyle = {
    fontWeight: 'bold',
    color: darkMode ? '#fff' : 'inherit',
  };

  // Style pour le drawer de filtres
  const filterDrawerStyle = {
    width: 320,
    flexShrink: 0,
    '& .MuiDrawer-paper': {
      width: 320,
      boxSizing: 'border-box',
      p: 2,
      bgcolor: darkMode ? 'background.paper' : '#fff',
      transition: 'background-color 0.3s ease',
    },
  };

  // Styles pour les cartes de statistiques
  const statCardStyle = {
    p: 2,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    borderRadius: 2,
    boxShadow: 3,
    bgcolor: darkMode ? 'background.paper' : '#fff',
    transition: 'background-color 0.3s ease',
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 3) loadPendingValidations();
  };

  const loadPendingValidations = async () => {
    if (!currentUser) return;
    setLoadingValidations(true);
    try {
      const [validations, validated, servicesSnap] = await Promise.all([
        sectorService.getPendingValidations(currentUser.uid),
        sectorService.getValidatedByDemandeur(currentUser.uid),
        getDocs(collection(db, COLLECTIONS.SERVICES)),
      ]);
      setPendingValidations(validations);
      setRecentlyValidated(validated.sort((a, b) => {
        const aT = a.validatedAt?.toMillis?.() || 0;
        const bT = b.validatedAt?.toMillis?.() || 0;
        return bT - aT;
      }));
      setValidationServices(servicesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const init = {};
      validations.forEach(v => { init[v.id] = { priority: 'medium', serviceId: '' }; });
      setValidationPriority(init);
    } catch (err) {
      console.error('loadPendingValidations:', err);
    } finally {
      setLoadingValidations(false);
    }
  };

  const handleValidate = async (requestId) => {
    const meta = validationPriority[requestId] || {};
    if (!meta.serviceId) {
      setError('Veuillez choisir un service technique avant de valider.');
      return;
    }
    try {
      await updateDoc(doc(db, COLLECTIONS.WORK_REQUESTS, requestId), {
        status: 'pending',
        priority: meta.priority || 'medium',
        assignedServiceId: meta.serviceId,
        requestingServiceId: currentUser.uid,
        validatedBy: currentUser.uid,
        validatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setPendingValidations(prev => prev.filter(r => r.id !== requestId));
    } catch (err) {
      console.error('handleValidate:', err);
      setError('Erreur lors de la validation.');
    }
  };

  const handleReject = async (requestId) => {
    if (!window.confirm('Rejeter ce signalement ? Il sera supprimé.')) return;
    try {
      await updateDoc(doc(db, COLLECTIONS.WORK_REQUESTS, requestId), {
        status: 'cancelled',
        updatedAt: serverTimestamp(),
      });
      setPendingValidations(prev => prev.filter(r => r.id !== requestId));
    } catch (err) {
      console.error('handleReject:', err);
    }
  };

  const handleFeedbackTabChange = (event, newValue) => {
    setFeedbackTabValue(newValue);
  };
  
  // Gestion de la pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Gestion du tri
  const handleSortClick = (event) => {
    setSortMenuAnchor(event.currentTarget);
  };
  
  const handleSortClose = () => {
    setSortMenuAnchor(null);
  };
  
  const handleSortChange = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
    handleSortClose();
  };
  
  // Gestion des filtres
  const handleFilterDrawerToggle = () => {
    setFilterDrawerOpen(!filterDrawerOpen);
  };
  
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => {
      if (filterType === 'dateRange') {
        return { ...prev, dateRange: value };
      }
      
      const currentValues = [...prev[filterType]];
      const valueIndex = currentValues.indexOf(value);
      
      if (valueIndex === -1) {
        currentValues.push(value);
      } else {
        currentValues.splice(valueIndex, 1);
      }
      
      return { ...prev, [filterType]: currentValues };
    });
  };
  
  const clearFilters = () => {
    setFilters({
      status: [],
      priority: [],
      category: [],
      dateRange: {
        start: null,
        end: null
      }
    });
  };
  
  // Extraction des catégories uniques pour les filtres
  const uniqueCategories = useMemo(() => {
    const categories = new Set();
    requests.forEach(request => {
      if (request.category) {
        categories.add(request.category);
      }
    });
    return Array.from(categories);
  }, [requests]);

  // Vérifier si les données en cache sont encore valides (5 minutes)
  const checkCache = useCallback(() => {
    if (cachedRequestsRef.current && cachedRequestsRef.current.timestamp > Date.now() - 5 * 60 * 1000) {
      return cachedRequestsRef.current.data;
    }
    return null;
  }, []);

  // Fonction pour récupérer les demandes avec pagination
  const fetchRequests = useCallback(async (loadMore = false) => {
    try {
      if (!loadMore) {
        // Vérifier le cache d'abord pour le chargement initial
        const cachedData = checkCache();
        if (cachedData) {
          console.log('Utilisation des données en cache');
          setRequests(cachedData);
          setLoading(false);
          return;
        }
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      setError('');

      // Vérifier si l'utilisateur est autorisé
      if (!currentUser) {
        setError('Vous devez être connecté pour accéder à cette page.');
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      // Requête 1 : demandes créées par le demandeur
      const ownQuery = query(
        collection(db, COLLECTIONS.WORK_REQUESTS),
        where('createdBy', '==', currentUser.uid),
        orderBy('createdAt', 'desc'),
        limit(20)
      );

      // Requête 2 : demandes QR validées par le demandeur (assignedDemandeurId)
      const validatedQuery = query(
        collection(db, COLLECTIONS.WORK_REQUESTS),
        where('assignedDemandeurId', '==', currentUser.uid)
      );

      const [ownSnap, validatedSnap] = await Promise.all([
        getDocs(ownQuery),
        getDocs(validatedQuery),
      ]);

      setHasMore(ownSnap.docs.length === 20);

      const toRequest = (d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate() || null,
        updatedAt: d.data().updatedAt?.toDate() || null,
      });

      // Fusionner en évitant les doublons et en excluant pending_validation (géré dans l'onglet À valider)
      const ownData = ownSnap.docs.map(toRequest);
      const validatedData = validatedSnap.docs
        .map(toRequest)
        .filter(r => r.status !== 'pending_validation' && !ownData.find(o => o.id === r.id));

      const requestsData = [...ownData, ...validatedData]
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      // Récupérer les demandes récentes (5 dernières)
      const recentQ = query(
        collection(db, COLLECTIONS.WORK_REQUESTS),
        where('createdBy', '==', currentUser.uid),
        orderBy('createdAt', 'desc'),
        limit(5)
      );

      const recentSnapshot = await getDocs(recentQ);
      const recentData = recentSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || null,
        updatedAt: doc.data().updatedAt?.toDate() || null
      }));

      // Calculer les statistiques
      const statusStats = {};
      const priorityStats = {};
      const monthlyStats = new Array(12).fill(0);
      let totalResolutionTime = 0;
      let completedCount = 0;
      let pendingCount = 0;

      requestsData.forEach(request => {
        // Statistiques par statut
        const status = request.status || 'Non défini';
        statusStats[status] = (statusStats[status] || 0) + 1;

        // Statistiques par priorité
        const priority = request.priority || 'Non défini';
        priorityStats[priority] = (priorityStats[priority] || 0) + 1;

        // Statistiques par mois
        if (request.createdAt) {
          const month = request.createdAt.getMonth();
          monthlyStats[month]++;
        }

        // Calcul du temps de résolution moyen
        if (request.status === 'completed' && request.createdAt && request.completedAt) {
          const resolutionTime = differenceInDays(request.completedAt, request.createdAt);
          totalResolutionTime += resolutionTime;
          completedCount++;
        }

        // Comptage des demandes en attente
        if (request.status === 'pending' || request.status === 'in_progress') {
          pendingCount++;
        }
      });

      // Préparer les données pour le graphique mensuel
      const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
      const monthlyData = monthlyStats.map((value, index) => ({
        month: monthNames[index],
        count: value
      }));

      setStats({
        byStatus: statusStats,
        byPriority: priorityStats,
        byMonth: monthlyData,
        avgResolutionTime: completedCount > 0 ? (totalResolutionTime / completedCount).toFixed(1) : 0,
        totalRequests: requestsData.length,
        pendingRequests: pendingCount,
        completedRequests: completedCount
      });

      // Mettre à jour l'état des demandes
      if (loadMore) {
        setRequests(prev => [...prev, ...requestsData]);
      } else {
        setRequests(requestsData);
        
        // Mettre en cache les données
        cachedRequestsRef.current = {
          data: requestsData,
          timestamp: Date.now()
        };
        
        // Réinitialiser le cache après 5 minutes
        if (cacheTimeoutRef.current) {
          clearTimeout(cacheTimeoutRef.current);
        }
        cacheTimeoutRef.current = setTimeout(() => {
          cachedRequestsRef.current = null;
        }, 5 * 60 * 1000);
      }
      setRecentRequests(recentData);
    } catch (error) {
      console.error('Erreur lors de la récupération des demandes:', error);
      setError('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [currentUser, checkCache, db]);

  // Charger les données initiales
  useEffect(() => {
    fetchRequests(false);
    
    // Nettoyer le timeout à la destruction du composant
    return () => {
      if (cacheTimeoutRef.current) {
        clearTimeout(cacheTimeoutRef.current);
      }
    };
  }, [currentUser, fetchRequests]);
  
  // Fonction pour charger plus de données
  const loadMoreRequests = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchRequests(true);
    }
  }, [loadingMore, hasMore, fetchRequests]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return '#ef5350';
      case 'medium':
        return '#fb8c00';
      case 'low':
        return '#66bb6a';
      default:
        return '#757575';
    }
  };

  const getStatusTranslation = (status) => {
    switch (status) {
      case 'completed':
        return 'Terminé';
      case 'in_progress':
        return 'En cours';
      case 'pending':
        return 'En attente';
      case 'cancelled':
        return 'Annulé';
      default:
        return status;
    }
  };

  const getPriorityTranslation = (priority) => {
    switch (priority) {
      case 'high':
        return 'Haute';
      case 'medium':
        return 'Moyenne';
      case 'low':
        return 'Basse';
      default:
        return priority;
    }
  };

  const filteredAndSortedRequests = useMemo(() => {
    // Filtrage par terme de recherche
    let result = requests.filter(request => {
      if (!searchTerm) return true;
      
      const searchLower = searchTerm.toLowerCase();
      return (
        (request.title && request.title.toLowerCase().includes(searchLower)) ||
        (request.description && request.description.toLowerCase().includes(searchLower)) ||
        (request.location && JSON.stringify(request.location).toLowerCase().includes(searchLower)) ||
        (request.category && request.category.toLowerCase().includes(searchLower))
      );
    });
    
    // Filtrage avancé
    result = result.filter(request => {
      // Filtre par statut
      if (filters.status.length > 0 && !filters.status.includes(request.status)) {
        return false;
      }
      
      // Filtre par priorité
      if (filters.priority.length > 0 && !filters.priority.includes(request.priority)) {
        return false;
      }
      
      // Filtre par catégorie
      if (filters.category.length > 0 && !filters.category.includes(request.category)) {
        return false;
      }
      
      // Filtre par date
      if (filters.dateRange.start && request.createdAt < filters.dateRange.start) {
        return false;
      }
      if (filters.dateRange.end && request.createdAt > filters.dateRange.end) {
        return false;
      }
      
      return true;
    });
    
    // Tri
    if (sortConfig.key) {
      result.sort((a, b) => {
        if (!a[sortConfig.key] && !b[sortConfig.key]) return 0;
        if (!a[sortConfig.key]) return 1;
        if (!b[sortConfig.key]) return -1;
        
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return result;
  }, [requests, searchTerm, filters, sortConfig]);
  
  // Pagination des résultats
  const paginatedRequests = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredAndSortedRequests.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedRequests, page, rowsPerPage]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Tableau de Bord Demandeur
        </Typography>
        <Box>
          <NotificationBell />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setTabValue(2)}
          >
            Nouvelle demande
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Cartes de statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={statCardStyle}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Total des demandes
            </Typography>
            <Typography variant="h3" component="div" sx={{ flexGrow: 1 }}>
              {stats.totalRequests}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Depuis le début
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={statCardStyle}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Demandes en cours
            </Typography>
            <Typography variant="h3" component="div" color="primary" sx={{ flexGrow: 1 }}>
              {stats.pendingRequests}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              En attente de résolution
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={statCardStyle}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Demandes terminées
            </Typography>
            <Typography variant="h3" component="div" color="success.main" sx={{ flexGrow: 1 }}>
              {stats.completedRequests}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Interventions complétées
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={statCardStyle}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Temps moyen de résolution
            </Typography>
            <Typography variant="h3" component="div" sx={{ flexGrow: 1 }}>
              {stats.avgResolutionTime}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Jours par demande
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* ── Navigation par onglets ── */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Mes demandes" icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label="Feedback" icon={<HistoryIcon />} iconPosition="start" />
          <Tab label="Nouvelle demande" icon={<CreateIcon />} iconPosition="start" />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                À valider
                {pendingValidations.length > 0 && (
                  <Chip label={pendingValidations.length} size="small" color="warning"
                    sx={{ height: 18, fontSize: '0.7rem', fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }} />
                )}
              </Box>
            }
            icon={<QrCode2Icon />}
            iconPosition="start"
            onClick={() => loadPendingValidations()}
          />
        </Tabs>
      </Paper>

      {/* Demandes récentes (affiché uniquement sur l'onglet 0) */}
      {tabValue === 0 && <Paper sx={{ p: 2, mb: 3, bgcolor: darkMode ? 'background.paper' : '#fff', transition: 'background-color 0.3s ease' }}>
        <Typography variant="h6" gutterBottom>
          Demandes récentes
        </Typography>
        <Grid container spacing={2}>
          {recentRequests.length > 0 ? (
            recentRequests.map((request) => (
              <Grid item xs={12} sm={6} md={4} key={request.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" component="div" noWrap>
                      {request.title || `Demande #${request.id.substring(0, 8)}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {request.createdAt && request.createdAt instanceof Date && !isNaN(request.createdAt) ? format(request.createdAt, 'dd/MM/yyyy', { locale: fr }) : 'Date inconnue'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <Chip 
                        label={getStatusTranslation(request.status)} 
                        color={getStatusColor(request.status)} 
                        size="small" 
                      />
                      <Chip 
                        label={getPriorityTranslation(request.priority)} 
                        sx={{ backgroundColor: getPriorityColor(request.priority), color: 'white' }} 
                        size="small" 
                      />
                    </Box>
                    <Typography variant="body2" noWrap>
                      {request.description || 'Aucune description'}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => setSelectedRequest(request)}>
                      Voir détails
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                Aucune demande récente
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>}

      {tabValue === 0 && (
        <>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, maxWidth: '600px' }}>
              <TextField
                placeholder="Rechercher une demande..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1 }}
              />
              
              {/* Indicateur de filtres actifs */}
              {(filters.status.length > 0 || filters.priority.length > 0 || filters.category.length > 0) && (
                <Chip 
                  label={`${filters.status.length + filters.priority.length + filters.category.length} filtres actifs`}
                  onDelete={clearFilters}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SortIcon />}
                onClick={handleSortClick}
              >
                Trier
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterListIcon />}
                onClick={handleFilterDrawerToggle}
                color={Object.values(filters).some(f => Array.isArray(f) ? f.length > 0 : f.start || f.end) ? "primary" : "inherit"}
              >
                Filtrer
              </Button>
            </Box>
            
            {/* Menu de tri */}
            <Menu
              anchorEl={sortMenuAnchor}
              open={Boolean(sortMenuAnchor)}
              onClose={handleSortClose}
            >
              <MenuItem onClick={() => handleSortChange('createdAt')}>
                Date {sortConfig.key === 'createdAt' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </MenuItem>
              <MenuItem onClick={() => handleSortChange('title')}>
                Titre {sortConfig.key === 'title' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </MenuItem>
              <MenuItem onClick={() => handleSortChange('priority')}>
                Priorité {sortConfig.key === 'priority' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </MenuItem>
              <MenuItem onClick={() => handleSortChange('status')}>
                Statut {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </MenuItem>
            </Menu>
            
            {/* Drawer de filtres */}
            <Drawer
              anchor="right"
              open={filterDrawerOpen}
              onClose={handleFilterDrawerToggle}
            >
              <Box sx={{ width: 300, p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Filtres</Typography>
                  <IconButton onClick={handleFilterDrawerToggle}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="subtitle2" gutterBottom>Statut</Typography>
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox 
                      checked={filters.status.includes('pending')} 
                      onChange={() => handleFilterChange('status', 'pending')} 
                    />}
                    label="En attente"
                  />
                  <FormControlLabel
                    control={<Checkbox 
                      checked={filters.status.includes('in_progress')} 
                      onChange={() => handleFilterChange('status', 'in_progress')} 
                    />}
                    label="En cours"
                  />
                  <FormControlLabel
                    control={<Checkbox 
                      checked={filters.status.includes('completed')} 
                      onChange={() => handleFilterChange('status', 'completed')} 
                    />}
                    label="Terminé"
                  />
                  <FormControlLabel
                    control={<Checkbox 
                      checked={filters.status.includes('cancelled')} 
                      onChange={() => handleFilterChange('status', 'cancelled')} 
                    />}
                    label="Annulé"
                  />
                </FormGroup>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2" gutterBottom>Priorité</Typography>
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox 
                      checked={filters.priority.includes('high')} 
                      onChange={() => handleFilterChange('priority', 'high')} 
                    />}
                    label="Haute"
                  />
                  <FormControlLabel
                    control={<Checkbox 
                      checked={filters.priority.includes('medium')} 
                      onChange={() => handleFilterChange('priority', 'medium')} 
                    />}
                    label="Moyenne"
                  />
                  <FormControlLabel
                    control={<Checkbox 
                      checked={filters.priority.includes('low')} 
                      onChange={() => handleFilterChange('priority', 'low')} 
                    />}
                    label="Basse"
                  />
                </FormGroup>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2" gutterBottom>Catégorie</Typography>
                <FormGroup>
                  {uniqueCategories.map(category => (
                    <FormControlLabel
                      key={category}
                      control={<Checkbox 
                        checked={filters.category.includes(category)} 
                        onChange={() => handleFilterChange('category', category)} 
                      />}
                      label={category}
                    />
                  ))}
                </FormGroup>
                
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <Button variant="outlined" onClick={clearFilters}>
                    Réinitialiser
                  </Button>
                  <Button variant="contained" onClick={handleFilterDrawerToggle}>
                    Appliquer
                  </Button>
                </Box>
              </Box>
            </Drawer>
          </Box>

          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="tableau des demandes">
              <TableHead>
                <TableRow>
                  <TableCell>Titre</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Priorité</TableCell>
                  <TableCell>Catégorie</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRequests.length > 0 ? (
                  paginatedRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell component="th" scope="row">
                        {request.title || `Demande #${request.id.substring(0, 8)}`}
                      </TableCell>
                      <TableCell>
                        {request.createdAt && request.createdAt instanceof Date && !isNaN(request.createdAt)
                          ? format(request.createdAt, 'dd/MM/yyyy', { locale: fr })
                          : 'Date inconnue'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusTranslation(request.status)}
                          color={getStatusColor(request.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getPriorityTranslation(request.priority)}
                          sx={{ backgroundColor: getPriorityColor(request.priority), color: 'white' }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{
                        (() => {
                          const cat = request.category || request.problem;
                          if (!cat) return 'Non catégorisé';
                          return ({plumbing:'Plomberie',electrical:'Électricité',electricity:'Électricité',carpentry:'Menuiserie',painting:'Peinture',hvac:'Climatisation',it:'Informatique',cleaning:'Nettoyage',gardening:'Jardinage',locksmith:'Serrurerie',safety:'Sécurité / Urgence',other:'Autre'}[cat.toLowerCase()] || cat);
                        })()
                      }</TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => setSelectedRequest(request)}>
                          Détails
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {searchTerm || Object.values(filters).some(f => Array.isArray(f) ? f.length > 0 : f.start || f.end) ? 'Aucun résultat trouvé' : 'Aucune demande disponible'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredAndSortedRequests.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Lignes par page:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
            />
            
            {/* Bouton pour charger plus de données */}
            {hasMore && (
              <Button 
                variant="outlined" 
                onClick={loadMoreRequests} 
                disabled={loadingMore}
                sx={{ ml: 2 }}
              >
                {loadingMore ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Chargement...
                  </>
                ) : (
                  'Charger plus'
                )}
              </Button>
            )}
          </Box>

          {/* Graphiques de statistiques */}
          <Grid container spacing={3} sx={{ mt: 4 }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: '100%', bgcolor: darkMode ? 'background.paper' : '#fff', transition: 'background-color 0.3s ease' }}>
                <Typography variant="h6" gutterBottom>
                  Demandes par statut
                </Typography>
                {Object.keys(stats.byStatus).length > 0 ? (
                  <PieChart
                    colors={darkMode ? ['#8884d8', '#82ca9d', '#ffc658', '#ff8042'] : undefined}
                    series={[
                      {
                        data: Object.entries(stats.byStatus).map(([status, count]) => ({
                          id: status,
                          value: count,
                          label: getStatusTranslation(status),
                          color: getStatusColor(status) === 'default' ? '#757575' : undefined,
                        })),
                        innerRadius: 30,
                        paddingAngle: 2,
                        cornerRadius: 4,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                      },
                    ]}
                    height={200}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 5 }}>
                    Aucune donnée disponible
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: '100%', bgcolor: darkMode ? 'background.paper' : '#fff', transition: 'background-color 0.3s ease' }}>
                <Typography variant="h6" gutterBottom>
                  Demandes par priorité
                </Typography>
                {Object.keys(stats.byPriority).length > 0 ? (
                  <PieChart
                    colors={darkMode ? ['#ff8042', '#ffc658', '#82ca9d'] : undefined}
                    series={[
                      {
                        data: Object.entries(stats.byPriority).map(([priority, count]) => ({
                          id: priority,
                          value: count,
                          label: getPriorityTranslation(priority),
                          color: getPriorityColor(priority),
                        })),
                        innerRadius: 30,
                        paddingAngle: 2,
                        cornerRadius: 4,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                      },
                    ]}
                    height={200}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 5 }}>
                    Aucune donnée disponible
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: '100%', bgcolor: darkMode ? 'background.paper' : '#fff', transition: 'background-color 0.3s ease' }}>
                <Typography variant="h6" gutterBottom>
                  Demandes par mois
                </Typography>
                {stats.byMonth.length > 0 ? (
                  <BarChart
                    colors={darkMode ? ['#8884d8'] : undefined}
                    dataset={stats.byMonth}
                    xAxis={[{ scaleType: 'band', dataKey: 'month' }]}
                    series={[{ dataKey: 'count', label: 'Demandes' }]}
                    height={200}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 5 }}>
                    Aucune donnée disponible
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {tabValue === 1 && (
        <RequesterFeedbackList />
      )}
      
      {tabValue === 2 && (
        <NewWorkRequestForm 
          onRequestCreated={(newRequest) => {
            // Mettre à jour la liste des demandes avec la nouvelle demande
            setRequests(prev => [newRequest, ...prev]);
            setRecentRequests(prev => {
              const updated = [newRequest, ...prev];
              return updated.slice(0, 3); // Garder seulement les 3 plus récentes
            });
            
            // Mettre à jour les statistiques
            setStats(prev => ({
              ...prev,
              totalRequests: prev.totalRequests + 1,
              pendingRequests: prev.pendingRequests + 1,
              byStatus: {
                ...prev.byStatus,
                pending: (prev.byStatus.pending || 0) + 1
              },
              byPriority: {
                ...prev.byPriority,
                [newRequest.priority]: (prev.byPriority[newRequest.priority] || 0) + 1
              }
            }));
            
            // Rediriger vers l'onglet des demandes après création
            setTabValue(0);
          }}
        />
      )}


      {/* ── Onglet À valider ── */}
      {tabValue === 3 && (
        <Box>
          <Typography variant="h6" sx={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, mb: 2 }}>
            Signalements à valider
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Ces signalements ont été envoyés via QR code par des personnes de votre secteur.
            Complétez les informations et validez pour les envoyer au service technique.
          </Typography>

          {loadingValidations && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}

          {!loadingValidations && pendingValidations.length === 0 && (
            <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">Aucun signalement en attente</Typography>
              <Typography variant="body2" color="text.disabled">
                Les signalements scannés via QR code apparaîtront ici.
              </Typography>
            </Paper>
          )}

          <Grid container spacing={2}>
            {pendingValidations.map(req => (
              <Grid item xs={12} md={6} key={req.id}>
                <Paper sx={{ p: 3, borderRadius: 3, border: '2px solid', borderColor: 'warning.light' }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Chip label="À valider" color="warning" size="small" sx={{ fontWeight: 700 }} />
                    <Typography variant="caption" color="text.secondary">
                      {req.createdAt?.toDate ? req.createdAt.toDate().toLocaleDateString('fr-FR') : '—'}
                    </Typography>
                  </Box>

                  {/* Auteur */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PersonIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                    <Typography variant="body2" fontWeight={600}>{req.anonymousName || 'Anonyme'}</Typography>
                  </Box>

                  {/* Localisation */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LocationOnIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                    <Typography variant="body2">
                      {req.sectorName}
                      {req.location?.room ? ` — ${req.location.room}` : ''}
                    </Typography>
                  </Box>

                  {/* Problème */}
                  <Chip
                    label={PROBLEM_TRANSLATIONS[req.problem] || req.problem || 'Non précisé'}
                    size="small" variant="outlined" sx={{ mb: 1.5 }}
                  />

                  {/* Description */}
                  <Typography variant="body2" sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 2, lineHeight: 1.6 }}>
                    {req.description}
                  </Typography>

                  {/* Choix service + priorité */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={7}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Service technique *</InputLabel>
                        <Select
                          label="Service technique *"
                          value={validationPriority[req.id]?.serviceId || ''}
                          onChange={e => setValidationPriority(prev => ({
                            ...prev,
                            [req.id]: { ...prev[req.id], serviceId: e.target.value }
                          }))}
                        >
                          {validationServices.filter(s => s.type === 'technical' || s.role === 'technical').map(s => (
                            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Priorité</InputLabel>
                        <Select
                          label="Priorité"
                          value={validationPriority[req.id]?.priority || 'medium'}
                          onChange={e => setValidationPriority(prev => ({
                            ...prev,
                            [req.id]: { ...prev[req.id], priority: e.target.value }
                          }))}
                        >
                          <MenuItem value="low">Basse</MenuItem>
                          <MenuItem value="medium">Normale</MenuItem>
                          <MenuItem value="high">Haute</MenuItem>
                          <MenuItem value="urgent">Urgente</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {/* Actions */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained" color="success" size="small"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => handleValidate(req.id)}
                      sx={{ flex: 1 }}
                    >
                      Valider → Envoyer
                    </Button>
                    <Button
                      variant="outlined" color="error" size="small"
                      startIcon={<CancelIcon />}
                      onClick={() => handleReject(req.id)}
                    >
                      Rejeter
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* ── Signalements déjà traités ── */}
          {recentlyValidated.length > 0 && (
            <Box sx={{ mt: 5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <HistoryIcon sx={{ color: 'text.secondary' }} />
                <Typography variant="h6" sx={{ fontFamily: "'Syne', sans-serif", fontWeight: 700 }}>
                  Signalements traités ({recentlyValidated.length})
                </Typography>
              </Box>
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Secteur / Salle</TableCell>
                      <TableCell>Problème</TableCell>
                      <TableCell>Signalé par</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell>Validé le</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentlyValidated.map(req => (
                      <TableRow key={req.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{req.sectorName || '—'}</Typography>
                          {req.location?.room && (
                            <Typography variant="caption" color="text.secondary">{req.location.room}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={PROBLEM_TRANSLATIONS[req.problem] || req.problem || 'Non précisé'}
                            size="small" variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{req.anonymousName || 'Anonyme'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={STATUS_LABELS[req.status] || req.status}
                            size="small"
                            color={STATUS_COLORS[req.status] || 'default'}
                            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {req.validatedAt?.toDate
                              ? req.validatedAt.toDate().toLocaleDateString('fr-FR')
                              : req.updatedAt?.toDate
                                ? req.updatedAt.toDate().toLocaleDateString('fr-FR')
                                : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}

      {/* Dialogue de détails de la demande */}
      {selectedRequest && (
        <WorkRequestHandler
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onUpdateSuccess={() => {
            setSelectedRequest(null);
            fetchRequests();
          }}
        />
      )}
    </Container>
  );
};

export default RequesterDashboard;
